import React, { useState, useRef, useMemo } from 'react';
import Card from 'ui/views/cards/Card';
import TextField from 'ui/elements/form/TextField';
import { AuthorImage, asUserAuthor, asCompanyAuthor } from 'domain/shared/author';
import { userAPIUrls } from 'apis/CompanyAPI/users/usersApi';
import { getOrUndefined } from 'util/resource';
import { SelfUserProfile } from 'types/user';
import { PostableSourcesDTO } from 'types/content';
import { Menu, MenuItem, useMediaQuery } from '@mui/material';
import { useBreakpoints } from 'hooks/useBreakpoints';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import styled from '@emotion/styled';
import CompanyLogo from 'ui/elements/CompanyLogo';
import Avatar from 'ui/elements/avatars/Avatar';
import PostUpdateDialog from 'domain/Updates/company/PostUpdateDialog';
import PostCommunityUpdateDialog from 'domain/Updates/community/PostCommunityUpdateDialog';
import { useUiState } from 'apis/CompanyAPI/users/useUiState';
import { invalidate, invalidateStartsWith } from 'hooks/useSWR';
import { userDashboardKey } from 'pages/Dashboard/useUserDashboardData';
import { contentSpacing, halfSpacing, quarterSpacing, sectionSpacing } from 'ui/theme/themeConstants';
import { postInputFieldStyles } from 'domain/Updates/shared/postInputFieldStyles';
import useSessionStorage from 'hooks/useSessionStorage';
import { ActivityType, DASHBOARD_PULSE_SELECTED_FEED_KEY } from 'pages/Dashboard/sections/pulse/Pulse';
import { Link } from 'react-router-dom';
import { userUrls, companyUrls, communityUrls } from 'urls';
import GraphIcon from 'ui/elements/icons/GraphIcon';
import AddIcon from 'ui/elements/icons/AddIcon';
import MatchIcon from 'ui/elements/icons/MatchIcon';
import OfficeIcon from 'ui/elements/icons/OfficeIcon';
import CalendarIcon from 'ui/elements/icons/CalendarIcon';
import Dialog from 'ui/views/dialogs/Dialog';
import { VisibilitySelector } from 'domain/Updates/shared/Visibility/VisibilitySelector';
import { PostTo } from 'domain/Updates/shared/types';
import Button from 'ui/elements/buttons/Button';
import KpiBulkUpdateDialog from 'domain/kpis/KpiBulkUpdateDialog';
import KpiAPI from 'apis/KpiAPI';
import useResource from 'util/resource/useResource';
import { KpiWithDisplay } from 'domain/kpis/types';
import { CommunityUpdate, CompanyUpdate } from 'types/content';

const PortraitContainer = styled.div<{ hasMultipleSources: boolean }>`
  position: relative;
  cursor: ${props => (props.hasMultipleSources ? 'pointer' : 'default')};
`;

const AVATAR_SIZE = 44;

const ActionLinksContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  gap: ${props => (props.isMobile ? halfSpacing : sectionSpacing)};
  padding-top: ${contentSpacing};
  padding-left: ${props => (props.isMobile ? 0 : halfSpacing)};
  padding-right: ${props => (props.isMobile ? 0 : halfSpacing)};
  justify-content: ${props => (props.isMobile ? 'space-around' : 'flex-start')};
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${quarterSpacing};
`;

type PostingSource =
  | { type: 'user'; userId: string }
  | { type: 'company'; companyId: number }
  | { type: 'community'; communitySlug: string };

interface Props {
  className?: string;
  userProfile: SelfUserProfile;
  postableSources: PostableSourcesDTO;
  onNewUpdatePosted?: (updateId: string) => void;
}

export default function DashboardPostInputField({ className, userProfile, postableSources, onNewUpdatePosted }: Props) {
  const { uiState, saveUiState } = useUiState();
  const isNarrow = useMediaQuery('(max-width: 1500px)');
  const { isMobile } = useBreakpoints();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isMenuOpen = Boolean(anchorEl);

  // State for feed selection when posting as self
  const [selectedSelfFeed, setSelectedSelfFeed] = useState<PostTo>();
  const [showFeedSelector, setShowFeedSelector] = useState(false);

  // State and data for KPI dialog
  const [isKpiDialogOpen, setIsKpiDialogOpen] = useState(false);

  // Access feed selection to switch to "For you" after posting
  const [_selectedFeed, selectFeed] = useSessionStorage<ActivityType>(DASHBOARD_PULSE_SELECTED_FEED_KEY, 'For you');

  const { postingSources, selfFeeds } = useMemo(() => {
    const companySources = postableSources.companies.map(companySource => ({
      type: 'company' as const,
      companyId: companySource.profile.id,
      name: companySource.profile.name,
      imageUrl: companySource.profile.logoURL,
      company: companySource.profile,
    }));

    const communitySources = postableSources.communities.map(communitySource => ({
      type: 'community' as const,
      communitySlug: communitySource.profile.slug || '',
      name: communitySource.profile.name,
      imageUrl: communitySource.profile.logoURL,
      community: communitySource.profile,
    }));

    const userSource =
      postableSources.self.feeds.length > 0
        ? [
            {
              type: 'user' as const,
              userId: userProfile.cwUserId,
              name: userProfile.name,
              imageUrl: userProfile.imageUrl,
            },
          ]
        : [];

    const postingSources = [...companySources, ...communitySources, ...userSource];
    const selfFeeds = postableSources.self.feeds.filter(feed => feed.type === 'community');

    return { postingSources, selfFeeds };
  }, [postableSources, userProfile]);

  // Get selected source from UI state, or default to first company if available
  const selectedSourceId = getOrUndefined(uiState)?.dashboardPostingSource;

  const selectedSource = useMemo(() => {
    if (!postingSources.length) return null;

    // Try to find the saved selection
    if (selectedSourceId) {
      const saved = postingSources.find(source => {
        if (selectedSourceId.type === 'company' && source.type === 'company') {
          return selectedSourceId.companyId === source.companyId;
        } else if (selectedSourceId.type === 'community' && source.type === 'community') {
          return selectedSourceId.communitySlug === source.communitySlug;
        } else if (selectedSourceId.type === 'user' && source.type === 'user') {
          return selectedSourceId.userId === source.userId;
        }
        return false;
      });
      if (saved) return saved;
    }

    // Default to first company, then first community, or first source if neither
    const firstCompany = postingSources.find(s => s.type === 'company');
    const firstCommunity = postingSources.find(s => s.type === 'community');
    return firstCompany || firstCommunity || postingSources[0];
  }, [postingSources, selectedSourceId]);

  // Fetch KPIs for the selected company
  const { resource: kpisResource, mutate: mutateKpis } = useResource<KpiWithDisplay[]>(
    selectedSource?.type === 'company' ? KpiAPI.kpi.list(selectedSource.companyId) : undefined,
  );
  const kpis = getOrUndefined(kpisResource) || [];

  const handleSourceSelect = (source: (typeof postingSources)[0]) => {
    const sourceId: PostingSource =
      source.type === 'company'
        ? { type: 'company', companyId: source.companyId }
        : source.type === 'community'
          ? { type: 'community', communitySlug: source.communitySlug }
          : { type: 'user', userId: source.userId };

    saveUiState({ dashboardPostingSource: sourceId });
    setAnchorEl(null);
  };

  const handleSelectorClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(selectorRef.current);
  };

  const handleInputClick = () => {
    // Blur immediately to create the caret blink effect
    inputRef.current?.blur();

    // If posting as self and have multiple feeds, show feed selector first
    if (selectedSource?.type === 'user' && selfFeeds && selfFeeds.length > 1) {
      setShowFeedSelector(true);
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    // Ensure input is blurred when closing
    inputRef.current?.blur();
    setIsDialogOpen(false);
    setShowFeedSelector(false);
    setSelectedSelfFeed(undefined);
  };

  const handleUpdatePosted = (update?: CommunityUpdate | CompanyUpdate) => {
    if (update && onNewUpdatePosted) {
      onNewUpdatePosted(update.update.id);
    }
    invalidate(userDashboardKey);
    // Invalidate all feed cache keys so the new post appears
    invalidateStartsWith([userAPIUrls.feed()]);
    // Switch to "For you" feed
    selectFeed('For you');
    inputRef.current?.blur();
    setIsDialogOpen(false);
    setShowFeedSelector(false);
    setSelectedSelfFeed(undefined);
  };

  if (!selectedSource) {
    return null;
  }

  const author =
    selectedSource.type === 'company'
      ? asCompanyAuthor(userProfile, selectedSource.company)
      : selectedSource.type === 'community'
        ? asUserAuthor({ cwUserId: '', name: selectedSource.name, imageUrl: selectedSource.imageUrl })
        : asUserAuthor(userProfile);

  const placeholder = "What's happening?";

  return (
    <div className={className}>
      <Card>
        <div className="u-flex u-flex-align-center u-flex--gap-quarter">
          <PortraitContainer ref={selectorRef} hasMultipleSources={postingSources.length > 1}>
            <SelectorButton onClick={postingSources.length > 1 ? handleSelectorClick : undefined}>
              <AuthorImage author={author} size={AVATAR_SIZE} />
              {postingSources.length > 1 && <ChevronDownIcon color="grey" />}
            </SelectorButton>
          </PortraitContainer>
          <TextField
            inputRef={inputRef}
            onClick={handleInputClick}
            placeholder={placeholder}
            sx={postInputFieldStyles}
            inputProps={{ readOnly: true }}
          />
        </div>

        {/* Action links - contextual based on posting source */}
        {selectedSource.type === 'company' && selectedSource.company && (
          <ActionLinksContainer isMobile={isMobile}>
            <button className="text-link-small" onClick={() => setIsKpiDialogOpen(true)}>
              <GraphIcon className="u-quarter-spacing-right" fontSize="small" /> {isNarrow ? 'KPIs' : 'Update KPIs'}
            </button>
            <Link className="text-link-small" to={companyUrls.settings.inviteUser(selectedSource.company.slug)}>
              <AddIcon className="u-quarter-spacing-right" fontSize="small" />{' '}
              {isNarrow ? 'Invite' : 'Invite your network'}
            </Link>
            <Link className="text-link-small" to="/communities/investor-matching">
              <MatchIcon className="u-quarter-spacing-right" fontSize="small" />{' '}
              {isMobile ? 'Matching' : isNarrow ? 'Get matching' : 'Get investor matching'}
            </Link>
            <Link
              className="text-link-small"
              to={`${userUrls.dashboard.manage()}?companyId=${selectedSource.companyId}`}
            >
              <OfficeIcon className="u-quarter-spacing-right" fontSize="small" />{' '}
              {isNarrow ? 'Manage' : 'Manage profile'}
            </Link>
          </ActionLinksContainer>
        )}

        {selectedSource.type === 'community' && selectedSource.community && (
          <ActionLinksContainer isMobile={isMobile}>
            <Link className="text-link-small" to={communityUrls.events.create(selectedSource.community.slug)}>
              <CalendarIcon className="u-quarter-spacing-right" fontSize="small" /> Create event
            </Link>
            <Link className="text-link-small" to={communityUrls.manageMembers.invite(selectedSource.community.slug)}>
              <AddIcon className="u-quarter-spacing-right" fontSize="small" /> Invite your network
            </Link>
          </ActionLinksContainer>
        )}
      </Card>

      {/* Source selector menu */}
      <Menu
        open={isMenuOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {postingSources.some(s => s.type === 'company') && (
          <MenuItem disabled key="companies-header">
            <small>Your companies:</small>
          </MenuItem>
        )}
        {postingSources
          .filter(s => s.type === 'company')
          .map(source => (
            <MenuItem
              key={`company-${source.companyId}`}
              onClick={() => handleSourceSelect(source)}
              selected={selectedSource?.type === 'company' && selectedSource.companyId === source.companyId}
            >
              <div className="u-flex-align-center u-flex--gap-half">
                <CompanyLogo company={source.company} size={32} />
                <span>{source.name}</span>
              </div>
            </MenuItem>
          ))}
        {postingSources.some(s => s.type === 'community') && (
          <MenuItem disabled key="communities-header">
            <small>Your communities:</small>
          </MenuItem>
        )}
        {postingSources
          .filter(s => s.type === 'community')
          .map(source => (
            <MenuItem
              key={`community-${source.communitySlug}`}
              onClick={() => handleSourceSelect(source)}
              selected={selectedSource?.type === 'community' && selectedSource.communitySlug === source.communitySlug}
            >
              <div className="u-flex-align-center u-flex--gap-half">
                <Avatar size={32} imageUrl={source.imageUrl} />
                <span>{source.name}</span>
              </div>
            </MenuItem>
          ))}
        {postingSources.some(s => s.type === 'user') && (
          <MenuItem disabled key="user-header">
            <small>Post as yourself:</small>
          </MenuItem>
        )}
        {postingSources
          .filter(s => s.type === 'user')
          .map(source => (
            <MenuItem
              key={`user-${source.userId}`}
              onClick={() => handleSourceSelect(source)}
              selected={selectedSource?.type === 'user' && selectedSource.userId === source.userId}
            >
              <div className="u-flex-align-center u-flex--gap-half">
                <Avatar size={32} imageUrl={source.imageUrl} />
                <span>{source.name}</span>
              </div>
            </MenuItem>
          ))}
      </Menu>

      {/* Post update dialog - for company sources */}
      {selectedSource.type === 'company' && selectedSource.company && (
        <PostUpdateDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          company={selectedSource.company}
          onUpdatePosted={handleUpdatePosted}
        />
      )}

      {/* Post update dialog - for community sources */}
      {selectedSource.type === 'community' && selectedSource.community && (
        <PostCommunityUpdateDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          community={selectedSource.community}
          onUpdatePosted={handleUpdatePosted}
          postTo={{
            type: 'community',
            visibility: 'CommunityMembers',
            profile: selectedSource.community,
          }}
          initialPostingAs={{ type: 'community', community: selectedSource.community }}
        />
      )}

      {/* Feed selector dialog - for user sources with multiple feeds */}
      {selectedSource.type === 'user' && selfFeeds && selfFeeds.length > 1 && (
        <Dialog open={showFeedSelector} onClose={handleDialogClose}>
          <VisibilitySelector
            value={selectedSelfFeed}
            options={selfFeeds.map(feed => ({
              type: 'community' as const,
              visibility: feed.visibilityOptions[0],
              profile: feed.profile,
            }))}
            onChange={setSelectedSelfFeed}
            onClose={handleDialogClose}
            confirmAction={
              <Button
                disabled={!selectedSelfFeed}
                onClick={() => {
                  setShowFeedSelector(false);
                  setIsDialogOpen(true);
                }}
                kind="primary"
              >
                Next
              </Button>
            }
          />
        </Dialog>
      )}

      {/* Post update dialog - for user sources, post to their selected feed */}
      {selectedSource.type === 'user' && selfFeeds && selfFeeds.length > 0 && (
        <PostCommunityUpdateDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          community={
            selectedSelfFeed && selectedSelfFeed.type === 'community' ? selectedSelfFeed.profile : selfFeeds[0].profile
          }
          onUpdatePosted={handleUpdatePosted}
          postTo={
            selectedSelfFeed && selectedSelfFeed.type === 'community'
              ? selectedSelfFeed
              : {
                  type: 'community' as const,
                  visibility: selfFeeds[0].visibilityOptions[0],
                  profile: selfFeeds[0].profile,
                }
          }
          initialPostingAs={{ type: 'user', user: userProfile }}
        />
      )}

      {/* KPI bulk update dialog - for company sources */}
      {selectedSource.type === 'company' && selectedSource.company && (
        <KpiBulkUpdateDialog
          kpis={kpis}
          companyId={selectedSource.companyId}
          companySlug={selectedSource.company.slug}
          isOpen={isKpiDialogOpen}
          onClose={() => setIsKpiDialogOpen(false)}
          onSuccess={() => mutateKpis()}
        />
      )}
    </div>
  );
}
